import _ from 'lodash';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { getAssessmentSubjectDetails, getOrganizationDetails } from 'Api/UcrmTprm/UcrmTprmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { Role } from 'Components/Context/RBACContext';
import { ExceptionsListing } from 'Components/Exceptions/ExceptionsListing/ExceptionsListing';
import { submitApiRequestWithNewFiles, useFileManagementArea } from 'Components/Files/FileManagementArea';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { IssuesListing } from 'Components/Issues/IssuesListing/IssuesListing';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Modal } from 'Components/Modal/Modal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutQuestionnaire, SidebarSection as PageSidebarSection } from 'Components/PageLayout/PageLayoutQuestionnaire/PageLayoutQuestionnaire';
import { RequiresAttentionMarker } from 'Components/RequiresAttentionMarker/RequiresAttentionMarker';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { DiscussionThread } from 'Components/UcrmTprm/DiscussionThread/DiscussionThread';
import { AdvertiseDueDiligenceAiAnalysisModal, ConfirmDueDiligenceAiAnalysisModal } from 'Components/UcrmTprm/DueDiligenceAiAnalysisModal/DueDiligenceAiAnalysisModal';
import { ReviewForm } from 'Components/UcrmTprm/ReviewForm/ReviewForm';
import reviewFormStyles from 'Components/UcrmTprm/ReviewForm/ReviewForm.module.css';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { ICON_CLOSE, ICON_EDIT_MODIFY_UPDATE, ICON_SAVE, ICON_SUBMIT } from 'Config/Icons';
import { TPRM_ASSESSMENT, TPRM_ASSESSMENT_HISTORICAL, TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_QUESTIONNAIRE, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { TPRM_ASSESSMENT_DELEGATES, TPRM_ASSESSMENT_OWNER, UCRM_ASSESSMENT_DELEGATES, UCRM_ASSESSMENT_OWNER, UCRM_QUESTIONNAIRE_RESPONDENTS } from 'Config/Tooltips';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { iso8601ToUsDateShort, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getSummitGuideDueDiligenceFeature } from 'Helpers/UcrmTprm';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { AssessmentSubjectArchiveBase, AssessmentSubjectBase } from 'Models/AssessmentSubject';
import { DueDiligenceControl, SaveDueDiligenceControlGroupRequest, hasUnacceptableAnswer, isQuestionAnswered } from 'Models/DueDiligence';
import { ExceptionBreadcrumbSource, ExceptionResponse } from 'Models/Exceptions';
import { FileToBeUploaded, UploadedFile } from 'Models/Files';
import { IssueBreadcrumbSource, IssueResponse } from 'Models/Issues';
import { OrganizationArchiveBase, OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { getQuestionnaireRespondentIds } from 'Models/Ucrm';
import { TprmOrUcrmDueDiligenceAssessment, getArchivedAssessmentSubject, getArchivedOrganization } from 'Models/UcrmTprm';

import { ControlView } from './Components/ControlView/ControlView';
import { ControlsDashlet } from './Components/HeaderDashlets/ControlsDashlet';
import { QuestionsDashlet } from './Components/HeaderDashlets/QuestionsDashlet';
import { ManageAssessmentWideFilesModal } from './Components/ManageAssessmentWideFilesModal/ManageAssessmentWideFilesModal';
import { UpdateAssignmentsModal } from './Components/UpdateAssignmentsModal/UpdateAssignmentsModal';
import styles from './DueDiligenceAssessment.module.css';
import { useControlAssessmentStatistics } from './Hooks/useControlAssessmentStatistics';
import { useQuestionsStatistics } from './Hooks/useQuestionsStatistics';

export interface DueDiligenceAssessmentLoaderProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export const DueDiligenceAssessmentLoader = (props: DueDiligenceAssessmentLoaderProps) => {
    return <PageDataLoader path={TPRM_ASSESSMENT} fetchData={(urlParams) => Promise.all([getOrganizationDetails(props.moduleApi, urlParams.third_party_id), getAssessmentSubjectDetails(props.moduleApi, urlParams.third_party_id, urlParams.service_id), props.assessmentApi.getAssessment(urlParams.third_party_id, urlParams.service_id, urlParams.assessment_id)])} renderForm={(result) => <DueDiligenceAssessment artificialIntelligenceApi={props.artificialIntelligenceApi} authHandler={props.authHandler} initialAssessment={result[2].data} documentApi={props.documentApi} exceptions={result[0].data.exceptions} issues={result[0].data.issues} assessmentApi={props.assessmentApi} module={props.module} assessmentSubject={result[1].data} organization={result[0].data} />} />;
};

export interface DueDiligenceAssessmentHistoricalLoaderProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    module: DueDiligenceModule;
}

export const DueDiligenceAssessmentHistoricalLoader = (props: DueDiligenceAssessmentHistoricalLoaderProps) => {
    return (
        <PageDataLoader
            path={TPRM_ASSESSMENT_HISTORICAL}
            fetchData={(urlParams) => Promise.all([props.assessmentApi.getDueDiligenceWorkflowArchive(urlParams.third_party_id, urlParams.service_id, urlParams.timestamp), props.issuesApi.getIssuesByOrganization(urlParams.third_party_id), props.exceptionsApi.getExceptionsByOrganization(urlParams.third_party_id)])}
            renderForm={(result, urlParams) => {
                const historicalData = result[0].data;
                const assessment = historicalData.assessments.find((a) => a.id === urlParams.assessment_id)!;
                const issues = result[1].data;
                const exceptions = result[2].data;
                return <DueDiligenceAssessment artificialIntelligenceApi={props.artificialIntelligenceApi} authHandler={props.authHandler} initialAssessment={assessment} documentApi={props.documentApi} exceptions={exceptions} issues={issues} assessmentApi={props.assessmentApi} module={props.module} assessmentSubject={getArchivedAssessmentSubject(props.module, historicalData)} organization={getArchivedOrganization(props.module, historicalData)} historical={true} />;
            }}
        />
    );
};

export interface DueDiligenceAssessmentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    assessmentSubject: AssessmentSubjectBase | AssessmentSubjectArchiveBase;
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    exceptions: ExceptionResponse[];
    initialAssessment: TprmOrUcrmDueDiligenceAssessment;
    issues: IssueResponse[];
    module: DueDiligenceModule;
    organization: OrganizationResponseBase | OrganizationArchiveBase;

    historical?: boolean;
}

/**
 * Renders a due diligence assessment of a third-party service (TPRM) or use case (UCRM) for a SummIT user to complete.
 * Counterpart to the questionnaire pages where respondents answer questions (`ThirdPartyQuestionnaire` in TPRM, `AssessmentQuestionnaire` in UCRM, both built on `QuestionnaireForm`).
 */
const DueDiligenceAssessment = (props: DueDiligenceAssessmentProps): JSX.Element => {
    const cachedData = useCachedData();
    const userId = useMemo(() => props.authHandler.getAuthenticatedUserSubject()!, [props.authHandler]);
    const userIsAdmin = useMemo(() => props.authHandler.getUserRoles().includes(Role.ADMIN), [props.authHandler]);

    const issueIdToTitleMap = useMemo(() => {
        return new Map(props.issues.map((issue) => [issue.id, issue.title]));
    }, [props.issues]);

    const [assessment, setAssessment] = useState<TprmOrUcrmDueDiligenceAssessment>(props.initialAssessment);
    const [lastSavedAssessment, setLastSavedAssessment] = useState<TprmOrUcrmDueDiligenceAssessment>(props.initialAssessment);
    const [isSubmitting, setIsSubmitting] = useState(false);
    /** Tracks a failure when saving a control group, keyed by the group's identifier so the error is shown in that group's footer. */
    const [groupSaveError, setGroupSaveError] = useState<{ groupIdentifier: string; message: string }>();
    const [pendingNavigation, setPendingNavigation] = useState<{
        fromIndex: number;
        toIndex: number;
        resolve: (allowNavigation: boolean) => void;
    }>();

    /** Owned here rather than by `DiscussionThread` so that a partially written message survives the user switching to a different section of the assessment. */
    const [draftDiscussionMessage, setDraftDiscussionMessage] = useState('');

    const [allPendingFiles, setAllPendingFiles] = useState<File[]>([]);
    const [selectedPendingFilesByControl, setSelectedPendingFilesByControl] = useState<Map<string, File[]>>(new Map());

    const [showAssessmentWideFilesModal, setShowAssessmentWideFilesModal] = useState(false);
    const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
    const [showDelegatesModal, setShowDelegatesModal] = useState(false);
    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [showSummitGuideModal, setShowSummitGuideModal] = useState(false);

    const summitGuideAvailability = cachedData.checkAdvancedFeatureAvailability(getSummitGuideDueDiligenceFeature(props.module));

    const [reviewFormState, setReviewFormState] = useState({
        additionalInformation: props.initialAssessment.assessment_review_human.comments,
        overallEffectiveness: props.initialAssessment.assessment_review_human.effectiveness,
        residualRisk: props.initialAssessment.assessment_review_human.residual_risk,
    });

    const [submitReviewWithManagedFiles, reviewFileManagementHookValues, reviewHasUnsavedFileChanges, resetReviewFiles] = useFileManagementArea<TprmOrUcrmDueDiligenceAssessment>(props.documentApi, props.initialAssessment.assessment_review_human.files);

    const [reviewHasUnsavedChanges, onReviewSaved] = useUnsavedChangesTracking(reviewFormState, reviewHasUnsavedFileChanges);

    const [reviewApiRequestState, setReviewApiRequestState] = useState<'none' | 'saving' | 'submitting'>('none');
    const [reviewToastState, setReviewToastState] = useState<{ type: 'success' | 'failure'; message: string }>();
    /** Surfaces AI Content Advisor errors from control comment fields. A page-level toast is used because the control fields live in control-group sidebar sections, which are unmounted when another section is active. */
    const [aiErrorMessage, setAiErrorMessage] = useState<string>();

    const questionsStatistics = useQuestionsStatistics([lastSavedAssessment])[lastSavedAssessment.id];
    const controlAssessmentStatistics = useControlAssessmentStatistics([lastSavedAssessment])[lastSavedAssessment.id];

    /** Marks a discussion as read in the background. For immediate feedback, sets the "last read" timestamp in state to a time in the far future; the real timestamp will be loaded next time this page is loaded from scratch. */
    const markMessagesAsRead = useCallback(async () => {
        const farFutureTimestamp = jsDateToIso8601(new Date(9999, 0));
        const updatedAssessment = _.cloneDeep(assessment);
        updatedAssessment.discussion.marked_as_read_timestamps_client[userId] = farFutureTimestamp;
        setAssessment(updatedAssessment);

        props.assessmentApi.markDiscussionAsRead(assessment.third_party_id, assessment.service_id, assessment.id).catch((error) => {
            console.error('Failed to mark discussion as read:', error);
        });
    }, [assessment, props.assessmentApi, userId]);

    const lastReadTimestampForUser: string | undefined = assessment.discussion.marked_as_read_timestamps_client[userId];
    const unreadMessageCount = useMemo(() => {
        if (!lastReadTimestampForUser) {
            return assessment.discussion.messages.length;
        } else {
            return assessment.discussion.messages.filter((message) => message.timestamp > lastReadTimestampForUser).length;
        }
    }, [assessment.discussion.messages, lastReadTimestampForUser]);

    const saveGroup = useCallback(
        async (frameworkIdentifier: string, groupIdentifier: string) => {
            setIsSubmitting(true);

            try {
                const frameworkIndex = assessment.control_frameworks.findIndex((f) => f.identifier === frameworkIdentifier);
                const groupIndex = assessment.control_frameworks[frameworkIndex].control_groups.findIndex((g) => g.identifier === groupIdentifier);

                const submitApiRequest = async (getFilesToBeUploadedForControl: (controlIdentifier: string) => FileToBeUploaded[]): Promise<TprmOrUcrmDueDiligenceAssessment> => {
                    const request: SaveDueDiligenceControlGroupRequest = {
                        updates: assessment.control_frameworks[frameworkIndex].control_groups[groupIndex].controls.map((control) => ({
                            control_identifier: control.identifier,
                            comments: control.control_assessment_human.comments,
                            effectiveness: control.control_assessment_human.effectiveness,
                            new_files: getFilesToBeUploadedForControl(control.identifier),
                            selected_existing_file_ids: control.control_assessment_human.files.map((f) => f.file_id),
                        })),
                    };

                    const [controlFramework, controlGroup] = groupIdentifier.split('#');
                    return (await props.assessmentApi.saveControlGroup(assessment.third_party_id, assessment.service_id, assessment.id, controlFramework, controlGroup, request)).data;
                };

                const updatedAssessment = await submitApiRequestWithNewFiles(props.documentApi, selectedPendingFilesByControl, submitApiRequest);

                setAssessment(updatedAssessment);
                setLastSavedAssessment(updatedAssessment);

                setAllPendingFiles([]);
                setSelectedPendingFilesByControl(new Map());
                setGroupSaveError(undefined);
            } catch (error) {
                setGroupSaveError({ groupIdentifier, message: error.message });
            } finally {
                setIsSubmitting(false);
            }
        },
        [assessment, props.assessmentApi, props.documentApi, selectedPendingFilesByControl]
    );

    /**
     * Saves or submits the review. Returns whether the operation succeeded, so callers (e.g. the "Save changes" flow when navigating away) can avoid proceeding on failure.
     * When the save is triggered by navigating away from the Review section, `suppressSuccessToast` should be set so the "Review saved." toast isn't shown when the user navigates back to the Review section.
     */
    const handleReviewSubmit = useCallback(
        async (isSubmit: boolean, suppressSuccessToast = false): Promise<boolean> => {
            try {
                setReviewApiRequestState(isSubmit ? 'submitting' : 'saving');
                const updatedAssessment = await submitReviewWithManagedFiles(async (fileUpdates) => {
                    const response = await props.assessmentApi.saveOrSubmitAssessment(props.organization.id, props.assessmentSubject.id, props.initialAssessment.id, {
                        is_submit: isSubmit,
                        file_updates: fileUpdates,
                        comments: reviewFormState.additionalInformation,
                        overall_control_effectiveness: reviewFormState.overallEffectiveness,
                        residual_risk: reviewFormState.residualRisk,
                    });
                    return response.data;
                });

                setAssessment(updatedAssessment);
                setLastSavedAssessment(updatedAssessment);
                onReviewSaved(reviewFormState);

                // If saving (not submitting), show a success toast. When submitting, the success message will be shown in the success confirmation modal.
                if (!isSubmit && !suppressSuccessToast) {
                    setReviewToastState({
                        type: 'success',
                        message: 'Review saved.',
                    });
                }

                return true;
            } catch (error) {
                setReviewToastState({
                    type: 'failure',
                    message: error.message,
                });
                return false;
            } finally {
                setReviewApiRequestState('none');
            }
        },
        [props.assessmentApi, props.initialAssessment.id, props.assessmentSubject.id, props.organization.id, reviewFormState, onReviewSaved, submitReviewWithManagedFiles]
    );

    const sidebarSections: PageSidebarSection[] = useMemo(() => {
        // First section: Status, Assignments, and Discussion
        const assessmentStatusText: JSX.Element = (() => {
            if (assessment.assessment_submitted_timestamp) {
                return (
                    <Text>
                        The assessment was submitted{assessment.assessment_submitted_by ? ` by ${getUserNameFromSubject(assessment.assessment_submitted_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}` : ''} on {iso8601ToUsDateShort(assessment.assessment_submitted_timestamp)}.
                    </Text>
                );
            } else if (assessment.questionnaire_submitted_timestamp) {
                return (
                    <Text>
                        The questionnaire was marked as completed{assessment.questionnaire_submitted_by ? ` by ${props.module === ProductModule.UCRM ? getUserNameFromSubject(assessment.questionnaire_submitted_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) : assessment.questionnaire_submitted_by}` : ''} on {iso8601ToUsDateShort(assessment.questionnaire_submitted_timestamp)}.
                    </Text>
                );
            } else {
                return <Text>The questionnaire is in progress.</Text>;
            }
        })();

        // In UCRM, questionnaires are answered by SummIT users (the respondents) rather than by the third party.
        const respondentsCanStillSendMessagesText = props.module === ProductModule.UCRM ? "The questionnaire's respondents can still send discussion messages, but are no longer able to answer the questionnaire." : `${props.organization.name} can still send discussion messages, but is no longer able to respond to the questionnaire.`;
        const assessmentStatusExplanationText: JSX.Element = (() => {
            if (props.historical) {
                return <Text>This is a read-only view of a past assessment. No changes can be made.</Text>;
            } else if (assessment.assessment_submitted_timestamp) {
                return <Text>{respondentsCanStillSendMessagesText}</Text>;
            } else if (assessment.questionnaire_submitted_timestamp) {
                return (
                    <>
                        <Text>{respondentsCanStillSendMessagesText}</Text>
                        <Text>Review the responses in each control group before submitting the assessment.</Text>
                    </>
                );
            } else {
                return <Text>{`Responses to questions can be reviewed at this time, but they may change until ${props.module === ProductModule.UCRM ? "the questionnaire's respondents submit" : `${props.organization.name} marks`} the questionnaire as completed.`}</Text>;
            }
        })();

        const userCanAnswerQuestionnaire = props.module === ProductModule.UCRM && !props.historical && (userIsAdmin || getQuestionnaireRespondentIds(props.module, assessment).includes(userId));

        const overviewSection: PageSidebarSection = {
            title: 'Status, Assignments, and Discussion',
            content: [
                {
                    title: 'Status',
                    content: (
                        <>
                            {assessmentStatusText}
                            {assessmentStatusExplanationText}
                            {userCanAnswerQuestionnaire && (
                                <Link size="sm" to={generatePath(UCRM_QUESTIONNAIRE, { third_party_id: assessment.third_party_id, service_id: assessment.service_id, assessment_id: assessment.id })}>
                                    {assessment.questionnaire_submitted_timestamp ? 'View questionnaire' : 'Answer questionnaire'}
                                </Link>
                            )}
                        </>
                    ),
                },
                {
                    title: 'Assignments',
                    buttons: !props.historical ? (
                        <Button variant="secondary" onClick={() => setShowAssignmentsModal(true)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Update Assignments
                        </Button>
                    ) : undefined,
                    content: (
                        <div className={styles.assignmentsColumn}>
                            <div className={styles.assignmentsRow}>
                                <div className={styles.assignmentsRowElement}>
                                    <div className={styles.textAndTooltip}>
                                        <Text noStyles variant="Header4">
                                            Owner
                                        </Text>
                                        <FormFieldTooltip text={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_OWNER : TPRM_ASSESSMENT_OWNER} />
                                    </div>
                                    <Text noStyles>{getUserNameFromSubject(assessment.owner_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}</Text>
                                </div>
                                <div className={styles.assignmentsRowElement}>
                                    <div className={styles.textAndTooltip}>
                                        <Text noStyles variant="Header4">
                                            Delegates
                                        </Text>
                                        <FormFieldTooltip text={props.module === ProductModule.UCRM ? UCRM_ASSESSMENT_DELEGATES : TPRM_ASSESSMENT_DELEGATES} />
                                    </div>
                                    {assessment.delegate_ids.length === 0 ? (
                                        <Text noStyles>-</Text>
                                    ) : assessment.delegate_ids.length <= 5 ? (
                                        <Text noStyles>
                                            {assessment.delegate_ids
                                                .map((delegateId) => getUserNameFromSubject(delegateId, cachedData.users, UserNameFormat.FIRST_SPACE_LAST))
                                                .filter((name) => name !== undefined)
                                                .join(', ')}
                                        </Text>
                                    ) : (
                                        <Button variant="linkText" onClick={() => setShowDelegatesModal(true)} size="sm">
                                            {`${assessment.delegate_ids.length} delegates`}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {props.module === ProductModule.UCRM && (
                                <div className={styles.assignmentsRow}>
                                    <div className={styles.assignmentsRowElement}>
                                        <div className={styles.textAndTooltip}>
                                            <Text noStyles variant="Header4">
                                                Questionnaire Respondents
                                            </Text>
                                            <FormFieldTooltip text={UCRM_QUESTIONNAIRE_RESPONDENTS} />
                                        </div>
                                        <Text noStyles>
                                            {getQuestionnaireRespondentIds(props.module, assessment)
                                                .map((respondentId) => getUserNameFromSubject(respondentId, cachedData.users, UserNameFormat.FIRST_SPACE_LAST))
                                                .filter((name) => name !== undefined)
                                                .join(', ') || '-'}
                                        </Text>
                                    </div>
                                </div>
                            )}
                            <div className={styles.assignmentsRow}>
                                <div className={styles.assignmentsRowElement}>
                                    <Text variant="Header4">Questionnaire Due Date</Text>
                                    <Text noStyles>{assessment.questionnaire_due_date ? iso8601ToUsDateShort(assessment.questionnaire_due_date) : '-'}</Text>
                                </div>
                                <div className={styles.assignmentsRowElement}>
                                    <Text variant="Header4">Assessment Due Date</Text>
                                    <Text noStyles>{assessment.assessment_due_date ? iso8601ToUsDateShort(assessment.assessment_due_date) : '-'}</Text>
                                </div>
                            </div>
                        </div>
                    ),
                },
                {
                    title: 'Discussion',
                    content: (
                        <>
                            <Text>{`Until the ${props.module === ProductModule.UCRM ? "use case's" : "third-party service's"} Final Review is completed, messages may be sent between ${cachedData.clientDetails.client_name} and ${props.organization.name}.`}</Text>
                            {unreadMessageCount > 0 && !props.historical && (
                                <>
                                    <Text variant="Text3" color="darkGray" noStyles>
                                        Messages were added since you last viewed the discussion.
                                    </Text>
                                    <Button variant="linkText" onClick={markMessagesAsRead} size="sm">
                                        Mark as Read
                                    </Button>
                                </>
                            )}
                            <DiscussionThread perspective="assessment" assessment={assessment} assessmentApi={props.assessmentApi} discussion={assessment.discussion} module={props.module} onMessageSent={setAssessment} clientName={cachedData.clientDetails.client_name} organizationName={props.organization.name} users={cachedData.users} disabled={props.historical} draftMessage={draftDiscussionMessage} onDraftMessageChange={setDraftDiscussionMessage} />
                        </>
                    ),
                },
            ],
            additionalContent:
                unreadMessageCount > 0 ? (
                    <RequiresAttentionMarker type="warning" size="sm">
                        {`${unreadMessageCount} unread message${unreadMessageCount === 1 ? '' : 's'}`}
                    </RequiresAttentionMarker>
                ) : undefined,
        };

        // One section per control group
        const groupSections: PageSidebarSection[] = [];
        assessment.control_frameworks.forEach((framework, frameworkIndex) => {
            framework.control_groups.forEach((group, groupIndex) => {
                const lastSavedGroup = lastSavedAssessment.control_frameworks[frameworkIndex].control_groups[groupIndex];
                let unassessedCount = 0;
                for (const control of lastSavedGroup.controls) {
                    if (!control.control_assessment_human.effectiveness) {
                        unassessedCount++;
                    }
                }

                let updatedQuestionsCount = 0;
                for (const control of group.controls) {
                    const clua = control.last_updated_at;
                    if (clua && control.questions.some((q) => q.last_updated_at && q.last_updated_at > clua)) {
                        updatedQuestionsCount++;
                    }
                }

                let unansweredCount = 0;
                let requiredUnansweredCount = 0;
                let unacceptableAnswerCount = 0;
                for (const control of lastSavedGroup.controls) {
                    for (const question of control.questions) {
                        if (!isQuestionAnswered(question)) {
                            unansweredCount++;

                            if (question.is_required) {
                                requiredUnansweredCount++;
                            }
                        }

                        if (hasUnacceptableAnswer(question)) {
                            unacceptableAnswerCount++;
                        }
                    }
                }

                // Important: If more fields are added to controls in the future, they must be accounted for here.
                const hasUnsavedControlChanges = group.controls.some((control, controlIndex) => {
                    const lastSaved: DueDiligenceControl = lastSavedAssessment.control_frameworks[frameworkIndex].control_groups[groupIndex].controls[controlIndex];
                    const currentFileIds = control.control_assessment_human.files
                        .map((f) => f.file_id)
                        .sort()
                        .join(',');
                    const lastSavedFileIds = lastSaved.control_assessment_human.files
                        .map((f) => f.file_id)
                        .sort()
                        .join(',');
                    return control.control_assessment_human.effectiveness !== lastSaved.control_assessment_human.effectiveness || (control.control_assessment_human.comments ?? '') !== (lastSaved.control_assessment_human.comments ?? '') || currentFileIds !== lastSavedFileIds;
                });
                const hasUnsavedNewFiles = allPendingFiles.length > 0; // We shouldn't need to check pending files by control, since those are a subset of this data.
                const hasUnsavedChanges = hasUnsavedControlChanges || hasUnsavedNewFiles;

                groupSections.push({
                    title: group.name,
                    footerProps: {
                        buttons: (
                            <Button variant="primary" loadingText="Saving..." isLoading={isSubmitting} onClick={() => saveGroup(framework.identifier, group.identifier)} fontAwesomeImage={ICON_SAVE} disabled={props.historical}>
                                Save Control Group
                            </Button>
                        ),
                        content: (
                            <>
                                <Text variant="Text2" noStyles>
                                    {group.name}
                                </Text>
                                <Text variant="Text3" color="darkGray" noStyles>
                                    {group.description}
                                </Text>
                            </>
                        ),
                        hasUnsavedChanges,
                        errorMessage: groupSaveError?.groupIdentifier === group.identifier ? groupSaveError.message : undefined,
                    },
                    metadata: {
                        frameworkIdentifier: framework.identifier,
                        groupIdentifier: group.identifier,
                    },
                    content: [
                        {
                            content: (
                                <>
                                    <div className={styles.controlsContainer}>
                                        {group.controls.map((control, controlIndex) => (
                                            <ControlView
                                                key={control.identifier}
                                                artificialIntelligenceApi={props.artificialIntelligenceApi}
                                                assessmentApi={props.assessmentApi}
                                                assessmentId={assessment.id}
                                                assessmentSubjectId={props.assessmentSubject.id}
                                                control={control}
                                                disabled={props.historical}
                                                documentApi={props.documentApi}
                                                existingPendingFiles={allPendingFiles}
                                                issueIdToTitleMap={issueIdToTitleMap}
                                                lastSavedAssessment={lastSavedAssessment}
                                                module={props.module}
                                                onAiError={setAiErrorMessage}
                                                selectedPendingFiles={selectedPendingFilesByControl.get(control.identifier) ?? []}
                                                organizationId={props.organization.id}
                                                onAssessmentUpdated={(updatedAssessment) => {
                                                    setAssessment(updatedAssessment);
                                                    setLastSavedAssessment(updatedAssessment);
                                                }}
                                                onChange={(effectiveness, comments, mappedFiles) => {
                                                    const updatedAssessment: TprmOrUcrmDueDiligenceAssessment = _.cloneDeep(assessment);
                                                    updatedAssessment.control_frameworks[frameworkIndex].control_groups[groupIndex].controls[controlIndex].control_assessment_human.effectiveness = effectiveness;
                                                    updatedAssessment.control_frameworks[frameworkIndex].control_groups[groupIndex].controls[controlIndex].control_assessment_human.comments = comments;
                                                    if (mappedFiles !== undefined) {
                                                        updatedAssessment.control_frameworks[frameworkIndex].control_groups[groupIndex].controls[controlIndex].control_assessment_human.files = mappedFiles;
                                                    }
                                                    setAssessment(updatedAssessment);
                                                }}
                                                onSaveFileChanges={(newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => {
                                                    setAllPendingFiles((current) => [...current, ...newPendingFiles]);

                                                    setSelectedPendingFilesByControl((current) => {
                                                        const updated = new Map(current);
                                                        updated.set(control.identifier, selectedPendingFiles);
                                                        return updated;
                                                    });

                                                    setAssessment((currentAssessment) => {
                                                        const updatedAssessment = _.cloneDeep(currentAssessment);

                                                        for (const framework of updatedAssessment.control_frameworks) {
                                                            for (const group of framework.control_groups) {
                                                                for (const _control of group.controls) {
                                                                    if (_control.identifier === control.identifier) {
                                                                        _control.control_assessment_human.files = selectedUploadedFiles;
                                                                        return updatedAssessment;
                                                                    }
                                                                }
                                                            }
                                                        }

                                                        return updatedAssessment;
                                                    });
                                                }}
                                            />
                                        ))}
                                    </div>
                                </>
                            ),
                        },
                    ],
                    additionalContent:
                        unansweredCount > 0 || unassessedCount > 0 || updatedQuestionsCount > 0 || unacceptableAnswerCount > 0 ? (
                            <>
                                {unansweredCount > 0 && (
                                    <RequiresAttentionMarker type="warning" size="sm">
                                        {`${unansweredCount} unanswered question${unansweredCount === 1 ? '' : 's'}`}
                                    </RequiresAttentionMarker>
                                )}
                                {requiredUnansweredCount > 0 && (
                                    <RequiresAttentionMarker type="negative" size="sm">
                                        {`${requiredUnansweredCount} required question${requiredUnansweredCount === 1 ? '' : 's'}`}
                                    </RequiresAttentionMarker>
                                )}
                                {unassessedCount > 0 && (
                                    <RequiresAttentionMarker type="warning" size="sm">
                                        {`${unassessedCount} unassessed control${unassessedCount === 1 ? '' : 's'}`}
                                    </RequiresAttentionMarker>
                                )}
                                {updatedQuestionsCount > 0 && (
                                    <RequiresAttentionMarker type="warning" size="sm">
                                        {`${updatedQuestionsCount} updated answer${updatedQuestionsCount === 1 ? '' : 's'}`}
                                    </RequiresAttentionMarker>
                                )}
                                {unacceptableAnswerCount > 0 && (
                                    <RequiresAttentionMarker type="negative" size="sm">
                                        {`${unacceptableAnswerCount} unacceptable answer${unacceptableAnswerCount === 1 ? '' : 's'}`}
                                    </RequiresAttentionMarker>
                                )}
                            </>
                        ) : undefined,
                });
            });
        });

        // Review section
        const reviewSection: PageSidebarSection = {
            title: 'Review',
            metadata: {
                isReview: true,
            },
            content: [
                {
                    content: (
                        <IssuesListing
                            hideCreateButton={props.historical === true}
                            useHorizontalRuleBelowTitle
                            hideOrganizationInfo
                            type={props.module}
                            issues={props.issues}
                            documentApi={props.documentApi}
                            organizations={[props.organization]}
                            preselectedOrganizationIdForCreate={props.organization.id}
                            colorTheme="light"
                            createButtonVariant="secondaryButton"
                            createIssueOrigin={{
                                source: IssueBreadcrumbSource.ASSESSMENT,
                                module: props.module,
                                organizationId: props.organization.id,
                                assessmentSubjectId: props.assessmentSubject.id,
                                assessmentSubjectDisplayName: `${props.assessmentSubject.vendor_name} - ${props.assessmentSubject.name}`,
                                assessmentId: assessment.id,
                                assessmentName: assessment.name,
                            }}
                        />
                    ),
                },
                {
                    content: (
                        <ExceptionsListing
                            hideCreateButton={props.historical === true}
                            useHorizontalRuleBelowTitle
                            hideOrganizationInfo
                            type={props.module}
                            exceptions={props.exceptions}
                            documentApi={props.documentApi}
                            organizations={[props.organization]}
                            preselectedOrganizationIdForCreate={props.organization.id}
                            colorTheme="light"
                            createButtonVariant="secondaryButton"
                            createExceptionOrigin={{
                                source: ExceptionBreadcrumbSource.ASSESSMENT,
                                module: props.module,
                                organizationId: props.organization.id,
                                assessmentSubjectId: props.assessmentSubject.id,
                                assessmentSubjectDisplayName: `${props.assessmentSubject.vendor_name} - ${props.assessmentSubject.name}`,
                                assessmentId: assessment.id,
                                assessmentName: assessment.name,
                            }}
                        />
                    ),
                },
                {
                    title: 'Assessment-Wide Files',
                    subtitle: 'Manage the files that should be considered against all controls in the assessment.',
                    buttons: !props.historical ? (
                        <Button variant="secondary" onClick={() => setShowAssessmentWideFilesModal(true)} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                            Manage
                        </Button>
                    ) : undefined,
                    content:
                        assessment.assessment_wide_files.length > 0 ? (
                            <div className={styles.assessmentWideFilesList}>
                                {[...assessment.assessment_wide_files]
                                    .sort((fileA, fileB) => fileA.filename.localeCompare(fileB.filename))
                                    .map((file) => (
                                        <UploadedFileAndState key={file.file_id} documentApi={props.documentApi} file={file} onDownloadError={(message) => setReviewToastState({ type: 'failure', message })} />
                                    ))}
                            </div>
                        ) : (
                            <Text noStyles>No assessment-wide files have been added.</Text>
                        ),
                },
                {
                    title: 'Review',
                    content: (
                        <>
                            {reviewToastState !== undefined && <TextToast variant={reviewToastState.type} clearToast={() => setReviewToastState(undefined)} text={reviewToastState.message} />}
                            <ReviewForm
                                _type="Assessment"
                                artificialIntelligenceApi={props.artificialIntelligenceApi}
                                module={props.module}
                                onAiError={setAiErrorMessage}
                                disabled={props.historical}
                                documentApi={props.documentApi}
                                inherentRisk={props.assessmentSubject.assessment_workflow_data.irq_inherent_risk_score ?? RiskRating.UNRATED}
                                additionalInformation={reviewFormState.additionalInformation}
                                overallEffectiveness={reviewFormState.overallEffectiveness}
                                residualRisk={reviewFormState.residualRisk}
                                savedOverallEffectiveness={lastSavedAssessment.assessment_review_human.effectiveness}
                                savedResidualRisk={lastSavedAssessment.assessment_review_human.residual_risk}
                                fileManagementHookValues={reviewFileManagementHookValues}
                                onAdditionalInformationChange={(value) => setReviewFormState((prev) => ({ ...prev, additionalInformation: value || undefined }))}
                                onOverallEffectivenessChange={(value) => setReviewFormState((prev) => ({ ...prev, overallEffectiveness: value }))}
                                onResidualRiskChange={(value) => setReviewFormState((prev) => ({ ...prev, residualRisk: value }))}
                                onClickSummitGuideButton={() => setShowSummitGuideModal(true)}
                                aiReview={assessment.assessment_review_ai}
                                aiReviewMetadata={assessment.ai_analysis_metadata}
                            />
                            <div className={reviewFormStyles.saveButtons}>
                                {/* If the review has not been submitted, show a secondary Save button and a primary Submit button; if it has been submitted, only show a primary Save button. */}
                                <Button
                                    variant={assessment.assessment_submitted_timestamp === undefined ? 'secondary' : 'primary'}
                                    onClick={() => {
                                        if (assessment.assessment_submitted_timestamp !== undefined && (reviewFormState.residualRisk === undefined || reviewFormState.residualRisk === RiskRating.UNRATED)) {
                                            setReviewToastState({
                                                type: 'failure',
                                                message: 'Residual risk is required.',
                                            });
                                        } else {
                                            setReviewToastState(undefined);
                                            handleReviewSubmit(false);
                                        }
                                    }}
                                    fontAwesomeImage={ICON_SAVE}
                                    isLoading={reviewApiRequestState === 'saving'}
                                    loadingText="Saving..."
                                    disabled={props.historical}
                                >
                                    Save Review
                                </Button>
                                {assessment.assessment_submitted_timestamp === undefined && (userIsAdmin || userId === assessment.owner_id || userId === props.assessmentSubject.vendor_service_manager_user_id) && (
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            if (reviewFormState.residualRisk === undefined || reviewFormState.residualRisk === RiskRating.UNRATED) {
                                                setReviewToastState({
                                                    type: 'failure',
                                                    message: 'Residual risk is required.',
                                                });
                                            } else {
                                                setReviewToastState(undefined);
                                                setShowSubmitConfirmationModal(true);
                                            }
                                        }}
                                        fontAwesomeImage={ICON_SAVE}
                                        disabled={props.historical || reviewApiRequestState !== 'none'}
                                    >
                                        Submit Review
                                    </Button>
                                )}
                            </div>
                        </>
                    ),
                },
            ],
            additionalContent: !assessment.assessment_submitted_timestamp ? (
                <RequiresAttentionMarker type="warning" size="sm">
                    Not submitted
                </RequiresAttentionMarker>
            ) : undefined,
        };

        return [overviewSection, ...groupSections, reviewSection];
    }, [props.artificialIntelligenceApi, props.historical, props.module, props.organization, props.assessmentApi, props.issues, props.assessmentSubject, props.exceptions, props.documentApi, assessment, cachedData.users, cachedData.clientDetails.client_name, unreadMessageCount, markMessagesAsRead, reviewToastState, reviewFormState.additionalInformation, reviewFormState.overallEffectiveness, reviewFormState.residualRisk, reviewFileManagementHookValues, reviewApiRequestState, lastSavedAssessment, allPendingFiles, isSubmitting, groupSaveError, saveGroup, issueIdToTitleMap, selectedPendingFilesByControl, handleReviewSubmit, userId, userIsAdmin, draftDiscussionMessage]);

    const handleBeforeSectionChange = useCallback(
        (fromIndex: number, toIndex: number): Promise<boolean> => {
            const leavingSection = sidebarSections[fromIndex];

            // Control-group sections track unsaved changes via their footer; the Review section tracks them separately (it has no footer).
            const leavingHasUnsavedChanges = leavingSection.footerProps?.hasUnsavedChanges || (leavingSection.metadata?.isReview === true && reviewHasUnsavedChanges);

            if (leavingHasUnsavedChanges) {
                return new Promise((resolve) => {
                    setPendingNavigation({ fromIndex, toIndex, resolve });
                });
            }

            return Promise.resolve(true);
        },
        [sidebarSections, reviewHasUnsavedChanges]
    );

    const handleDiscardChanges = useCallback(() => {
        if (!pendingNavigation) {
            return;
        }

        const leavingSection = sidebarSections[pendingNavigation.fromIndex];
        if (leavingSection.metadata?.isReview === true) {
            setReviewFormState({
                additionalInformation: lastSavedAssessment.assessment_review_human.comments,
                overallEffectiveness: lastSavedAssessment.assessment_review_human.effectiveness,
                residualRisk: lastSavedAssessment.assessment_review_human.residual_risk,
            });
            resetReviewFiles();
        } else {
            setAssessment(lastSavedAssessment);
            setAllPendingFiles([]);
            setSelectedPendingFilesByControl(new Map());
        }

        pendingNavigation.resolve(true);
        setPendingNavigation(undefined);
    }, [lastSavedAssessment, pendingNavigation, sidebarSections, resetReviewFiles]);

    const handleCancelNavigation = useCallback(() => {
        if (!pendingNavigation) {
            return;
        }

        pendingNavigation.resolve(false);
        setPendingNavigation(undefined);
    }, [pendingNavigation]);

    const handleSaveAndContinue = useCallback(async () => {
        if (!pendingNavigation) {
            return;
        }

        const currentSection = sidebarSections[pendingNavigation.fromIndex];

        if (currentSection.metadata?.isReview === true) {
            const saved = await handleReviewSubmit(false, true);
            if (!saved) {
                // Save failed; cancel the navigation so the user stays on the Review section and can see the error toast.
                pendingNavigation.resolve(false);
                setPendingNavigation(undefined);
                return;
            }
        } else {
            const metadata = currentSection.metadata as { frameworkIdentifier: string; groupIdentifier: string } | undefined;
            if (metadata) {
                await saveGroup(metadata.frameworkIdentifier, metadata.groupIdentifier);
            }
        }

        pendingNavigation.resolve(true);
        setPendingNavigation(undefined);
    }, [pendingNavigation, saveGroup, sidebarSections, handleReviewSubmit]);

    return (
        <>
            {aiErrorMessage && <TextToast variant="failure" clearToast={() => setAiErrorMessage(undefined)} text={aiErrorMessage} />}
            {showAssignmentsModal && (
                <UpdateAssignmentsModal
                    assessment={assessment}
                    assessmentApi={props.assessmentApi}
                    hideModal={() => setShowAssignmentsModal(false)}
                    module={props.module}
                    onSuccess={(updatedAssessment) => {
                        setAssessment(updatedAssessment);
                        setLastSavedAssessment(updatedAssessment);
                    }}
                />
            )}
            {showAssessmentWideFilesModal && (
                <ManageAssessmentWideFilesModal
                    assessment={assessment}
                    assessmentApi={props.assessmentApi}
                    documentApi={props.documentApi}
                    hideModal={() => setShowAssessmentWideFilesModal(false)}
                    module={props.module}
                    onSuccess={(updatedAssessment) => {
                        setAssessment(updatedAssessment);
                        setLastSavedAssessment(updatedAssessment);
                    }}
                />
            )}
            {showDelegatesModal && <UsersModal header="Delegates" userIds={assessment.delegate_ids} hideModal={() => setShowDelegatesModal(false)} />}
            {showSubmitConfirmationModal && (
                <ConfirmationModal
                    operationType={ConfirmationModalOperationType.NON_DESTRUCTIVE}
                    headerText="Submit Assessment"
                    areYouSureText="Are you sure you want to submit the assessment?"
                    buttonText="Submit"
                    buttonLoadingText="Submitting..."
                    buttonIcon={ICON_SUBMIT}
                    performOperation={async () => {
                        await handleReviewSubmit(true);
                        return 'Assessment submitted.';
                    }}
                    hideModal={() => setShowSubmitConfirmationModal(false)}
                >
                    <Text>{`After submission, the assessment can still be edited until the ${props.module === ProductModule.UCRM ? "use case's" : "third-party service's"} final review is submitted.`}</Text>
                </ConfirmationModal>
            )}
            {showSummitGuideModal && (summitGuideAvailability.isAvailable ? <ConfirmDueDiligenceAiAnalysisModal type="Assessment" artificialIntelligenceApi={props.artificialIntelligenceApi} assessmentId={assessment.id} assessmentWideFiles={assessment.assessment_wide_files} client={cachedData.clientDetails} documentApi={props.documentApi} hideModal={() => setShowSummitGuideModal(false)} module={props.module} onAssessmentAnalysisRequested={setAssessment} assessmentSubject={props.assessmentSubject} /> : <AdvertiseDueDiligenceAiAnalysisModal hideModal={() => setShowSummitGuideModal(false)} module={props.module} reasonUnavailable={summitGuideAvailability.reason} />)}
            {pendingNavigation && (
                <Modal open header="Unsaved Changes">
                    <Text>There are unsaved changes that will be lost permanently if you navigate away.</Text>
                    <div className={styles.unsavedChangesNavigateChoiceButtons}>
                        <Button variant="danger" onClick={handleDiscardChanges} fontAwesomeImage={ICON_CLOSE}>
                            Discard changes
                        </Button>
                        <div className={styles.unsavedChangesNavigateChoiceButtonsRight}>
                            <Button variant="secondary" onClick={handleCancelNavigation} fontAwesomeImage={ICON_CLOSE}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSaveAndContinue} fontAwesomeImage={ICON_SAVE} isLoading={isSubmitting || reviewApiRequestState === 'saving'} loadingText="Saving...">
                                Save changes
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            {props.historical && <Alert type="warning">This assessment is from a historical due diligence workflow and cannot be edited.</Alert>}
            <PageLayoutQuestionnaire
                headerBreadcrumb={
                    <Breadcrumb>
                        <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING)}>{props.module}</BreadcrumbLink>
                        <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: assessment.third_party_id, service_id: assessment.service_id })}>{`${props.assessmentSubject.vendor_name} - ${props.assessmentSubject.name}`}</BreadcrumbLink>
                        <BreadcrumbText>Assessments</BreadcrumbText>
                        <BreadcrumbText>{assessment.name}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerDashlets={[
                    {
                        title: 'Questions',
                        content: <QuestionsDashlet pageVariant="dashboard" statistics={questionsStatistics} dueDate={assessment.questionnaire_due_date} />,
                    },
                    {
                        title: 'Controls',
                        content: <ControlsDashlet pageVariant="dashboard" statistics={controlAssessmentStatistics} dueDate={assessment.assessment_due_date} />,
                    },
                ]}
                headerTitle={assessment.name}
                onBeforeSectionChange={handleBeforeSectionChange}
                sidebarSections={sidebarSections}
                sidebarTitle="Sections"
            />
        </>
    );
};
